import apiClient from './apiClient';

// Helper to extract data from API response
const extractData = (response) => {
    if (response.data && response.data.data) {
        return response.data.data;
    }
    if (response.data && (response.data.id || response.data.success !== undefined)) {
        return response.data;
    }
    return response.data || response;
};

const patientService = {
    // ==================== PROFILE APIs ====================
    getProfile: async () => {
        try {
            const response = await apiClient.get('/patient/profile');
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching profile:', error);
            throw error.response?.data || error.message;
        }
    },

    updateProfile: async (profileData) => {
        try {
            const response = await apiClient.patch('/patient/profile', profileData);
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error updating profile:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== VISA APPLICATION APIs ====================

    // STEP 0: Get current active application
    getCurrentApplication: async () => {
        try {
            const response = await apiClient.get('/patient/visa-applications/current');
            return extractData(response);
        } catch (error) {
            // 404 means no active application - return null
            if (error.response?.status === 404) {
                return { data: null };
            }
            console.error('[PatientService] Error fetching current application:', error);
            throw error.response?.data || error.message;
        }
    },

    // STEP 1: Create or resume visa application
    createVisaApplication: async (applicationData) => {
        try {
            console.log('[PatientService] Creating/resuming visa application:', applicationData);
            console.log('[PatientService] Request payload:', JSON.stringify(applicationData, null, 2));

            const response = await apiClient.post('/patient/visa-applications', applicationData);
            console.log('[PatientService] Application response:', response.data);
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error creating visa application:', error);
            console.error('[PatientService] Error response:', error.response);
            console.error('[PatientService] Error response data:', error.response?.data);
            console.error('[PatientService] Error response status:', error.response?.status);

            // Better error message extraction
            if (error.response?.data) {
                const errorData = error.response.data;
                if (errorData.message) {
                    throw new Error(errorData.message);
                }
                if (errorData.detail) {
                    throw new Error(errorData.detail);
                }
                if (typeof errorData === 'string') {
                    throw new Error(errorData);
                }
                throw errorData;
            }
            throw error.message || 'Failed to create visa application';
        }
    },

    // STEP 2: Get visa application by ID
    getVisaApplicationById: async (applicationId) => {
        try {
            const response = await apiClient.get(`/patient/visa-applications/${applicationId}`);
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching application:', error);
            throw error.response?.data || error.message;
        }
    },

    // Alias for getVisaApplicationById
    getApplicationById: async (applicationId) => {
        return patientService.getVisaApplicationById(applicationId);
    },

    // List all visa applications
    getVisaApplications: async () => {
        try {
            const response = await apiClient.get('/patient/visa-applications');
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching visa applications:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== MEDICAL DETAILS APIs ====================

    // STEP 3: Create medical details
    createMedicalDetails: async (applicationId, medicalData) => {
        try {
            console.log('[PatientService] Creating medical details for application:', applicationId);
            const response = await apiClient.post(
                `/patient/visa-applications/${applicationId}/medical-details`,
                medicalData
            );
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error creating medical details:', error);
            throw error.response?.data || error.message;
        }
    },

    // STEP 3: Update medical details
    updateMedicalDetails: async (applicationId, medicalData) => {
        try {
            console.log('[PatientService] Updating medical details for application:', applicationId);
            const response = await apiClient.patch(
                `/patient/visa-applications/${applicationId}/medical-details`,
                medicalData
            );
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error updating medical details:', error);
            throw error.response?.data || error.message;
        }
    },

    // STEP 3: Get medical details
    getMedicalDetails: async (applicationId) => {
        try {
            const response = await apiClient.get(
                `/patient/visa-applications/${applicationId}/medical-details`
            );
            return extractData(response);
        } catch (error) {
            if (error.response?.status === 404) {
                return null; // No medical details yet
            }
            console.error('[PatientService] Error fetching medical details:', error);
            throw error.response?.data || error.message;
        }
    },

    // Legacy: Submit medical details (for backward compatibility)
    submitMedicalDetails: async (medicalData) => {
        try {
            console.log('[PatientService] Submitting medical details (legacy):', medicalData);
            const response = await apiClient.post('/patient/medical-details', medicalData);
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error submitting medical details:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== HOSPITAL APIs ====================

    // STEP 4: Get hospitals list
    getHospitals: async () => {
        try {
            const response = await apiClient.get('/patient/hospitals');
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching hospitals:', error);
            throw error.response?.data || error.message;
        }
    },

    // STEP 4: Select hospital for application
    selectHospital: async (applicationId, hospitalData) => {
        try {
            console.log('[PatientService] Selecting hospital for application:', applicationId);
            const response = await apiClient.post(
                `/patient/visa-applications/${applicationId}/hospital-selection`,
                hospitalData
            );
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error selecting hospital:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== DOCUMENT APIs ====================

    // STEP 5: Get documents for application
    getDocuments: async (applicationId) => {
        try {
            const response = await apiClient.get(
                `/patient/visa-applications/${applicationId}/documents`
            );
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching documents:', error);
            throw error.response?.data || error.message;
        }
    },

    uploadDocument: async (applicationId, formData) => {
        try {
            console.log('[PatientService] Uploading document for application:', applicationId);
            const response = await apiClient.post(
                `/patient/visa-applications/${applicationId}/documents`,
                formData
            );
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error uploading document:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== APPLICATION STATUS APIs ====================

    // STEP 6: Get application status (legacy endpoint)
    getApplicationStatus: async () => {
        try {
            const response = await apiClient.get('/patient/application-status');
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching application status:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== PAYMENT APIs ====================

    // STEP 7: Get payments
    getPayments: async () => {
        try {
            const response = await apiClient.get('/patient/payments');
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error fetching payments:', error);
            throw error.response?.data || error.message;
        }
    },

    // ==================== SUPPORT TICKET APIs ====================

    // STEP 8: Get support tickets
    getTickets: async () => {
        try {
            console.log('[PatientService] Fetching tickets from /patient/tickets');
            const response = await apiClient.get('/patient/tickets');
            console.log('[PatientService] Raw tickets response:', response);
            const extracted = extractData(response);
            console.log('[PatientService] Extracted tickets data:', extracted);
            return extracted;
        } catch (error) {
            console.error('[PatientService] Error fetching tickets:', error);
            console.error('[PatientService] Error response:', error.response);
            console.error('[PatientService] Error response data:', error.response?.data);
            throw error.response?.data || error.message;
        }
    },

    // STEP 8: Create support ticket
    createTicket: async (ticketData) => {
        try {
            // If ticketData is FormData, send as multipart/form-data
            // Otherwise, create FormData from object
            let formData;
            if (ticketData instanceof FormData) {
                formData = ticketData;
            } else {
                formData = new FormData();
                formData.append('subject', ticketData.subject || '');
                formData.append('message', ticketData.message || '');
                if (ticketData.attachment) {
                    // If attachment is a File object, append directly
                    if (ticketData.attachment instanceof File) {
                        formData.append('attachment', ticketData.attachment);
                    } else {
                        // If it's a base64 string or other format, handle accordingly
                        console.warn('[PatientService] Attachment should be a File object for multipart/form-data');
                    }
                }
            }

            console.log('[PatientService] Creating support ticket with FormData');
            console.log('[PatientService] FormData fields:', Array.from(formData.keys()));

            // Don't set Content-Type header - let browser set it with boundary for FormData
            const response = await apiClient.post('/patient/tickets', formData);
            return extractData(response);
        } catch (error) {
            console.error('[PatientService] Error creating ticket:', error);
            console.error('[PatientService] Error response:', error.response);
            console.error('[PatientService] Error response data:', error.response?.data);
            console.error('[PatientService] Error response status:', error.response?.status);

            // Return full error object so component can extract details
            throw error;
        }
    },

    // Get ticket details including conversation messages
    getTicketById: async (id) => {
        try {
            const response = await apiClient.get(`/patient/tickets/${id}`);
            return extractData(response);
        } catch (error) {
            console.error(`[PatientService] Error fetching ticket ${id}:`, error);
            throw error.response?.data || error.message;
        }
    },

    // Reply to an existing ticket
    replyToTicket: async (id, message) => {
        try {
            const response = await apiClient.post(`/patient/tickets/${id}/reply`, { message });
            return extractData(response);
        } catch (error) {
            console.error(`[PatientService] Error replying to ticket ${id}:`, error);
            throw error.response?.data || error.message;
        }
    },
};

export default patientService;
